import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, Globe } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="pt-40 pb-32 container mx-auto px-6 max-w-4xl">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-20"
      >
        <div className="inline-flex items-center space-x-2 bg-zinc-900 border border-white/10 px-4 py-1.5 rounded-full mb-8">
          <Shield className="w-3 h-3 text-primary" />
          <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase">Privacy & Data Protection</span>
        </div>
        <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 tracking-tighter leading-none">
          Privacy <span className="text-primary">Policy</span>
        </h1>
        <p className="text-zinc-400 text-lg">
          Last Updated: January 23, 2026
        </p>
      </motion.div>

      {/* Introduction */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 mb-12"
      >
        <div className="flex items-start space-x-4">
          <Lock className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-xl font-bold mb-3">Your Privacy Matters</h3>
            <p className="text-zinc-400 leading-relaxed">
              At DuoStack Systems, we are committed to protecting your privacy and handling your data with care and transparency. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Quick Overview */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
      >
        <QuickStat icon={<Shield className="w-5 h-5" />} title="Secure" desc="End-to-end encryption" />
        <QuickStat icon={<Eye className="w-5 h-5" />} title="Transparent" desc="Clear data practices" />
        <QuickStat icon={<UserCheck className="w-5 h-5" />} title="Controlled" desc="You own your data" />
      </motion.div>

      {/* Privacy Sections */}
      <div className="space-y-12">
        <Section 
          number="01"
          title="Information We Collect"
          icon={<Database className="w-5 h-5" />}
          content={
            <>
              <h4 className="font-bold text-white mb-3">Personal Information</h4>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Request a consultation or quote for our services</li>
                <li>Contact us via email, phone, or contact forms</li>
                <li>Subscribe to our newsletter or updates</li>
                <li>Engage with us through our website or social media</li>
              </ul>
              <p className="mt-4">This information may include:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Name and contact information (email, phone number, address)</li>
                <li>Company name and job title</li>
                <li>Project requirements and specifications</li>
                <li>Payment and billing information</li>
              </ul>

              <h4 className="font-bold text-white mb-3 mt-6">Automatically Collected Information</h4>
              <p>When you visit our website, we automatically collect certain information about your device, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>IP address and browser type</li>
                <li>Operating system and device information</li>
                <li>Pages visited and time spent on our site</li>
                <li>Referring website addresses</li>
                <li>Cookie data (see our Cookie Policy below)</li>
              </ul>
            </>
          }
        />

        <Section 
          number="02"
          title="How We Use Your Information"
          icon={<Eye className="w-5 h-5" />}
          content={
            <>
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li><strong className="text-white">Service Delivery:</strong> To provide, maintain, and improve our software development services</li>
                <li><strong className="text-white">Communication:</strong> To respond to your inquiries, send project updates, and provide customer support</li>
                <li><strong className="text-white">Billing:</strong> To process payments and send invoices</li>
                <li><strong className="text-white">Marketing:</strong> To send you newsletters, promotional materials, and service updates (with your consent)</li>
                <li><strong className="text-white">Analytics:</strong> To understand how visitors use our website and improve user experience</li>
                <li><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
                <li><strong className="text-white">Security:</strong> To detect, prevent, and address technical issues, fraud, and security incidents</li>
              </ul>
            </>
          }
        />

        <Section 
          number="03"
          title="Information Sharing and Disclosure"
          icon={<Globe className="w-5 h-5" />}
          content={
            <>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
              
              <h4 className="font-bold text-white mb-3 mt-6">Service Providers</h4>
              <p>We may share your information with trusted third-party service providers who assist us in:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Payment processing and billing</li>
                <li>Email delivery and marketing automation</li>
                <li>Cloud hosting and infrastructure</li>
                <li>Analytics and performance monitoring</li>
              </ul>
              <p className="mt-2">These providers are contractually obligated to protect your data and use it only for specified purposes.</p>

              <h4 className="font-bold text-white mb-3 mt-6">Legal Requirements</h4>
              <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas).</p>

              <h4 className="font-bold text-white mb-3 mt-6">Business Transfers</h4>
              <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to this Privacy Policy.</p>
            </>
          }
        />

        <Section 
          number="04"
          title="Data Security"
          icon={<Lock className="w-5 h-5" />}
          content={
            <>
              <p>We implement industry-standard security measures to protect your personal information, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure server infrastructure and access controls</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection best practices</li>
                <li>Multi-factor authentication for sensitive systems</li>
              </ul>
              <p className="mt-4">However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
            </>
          }
        />

        <Section 
          number="05"
          title="Cookies and Tracking Technologies"
          icon={<Database className="w-5 h-5" />}
          content={
            <>
              <p>We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Remember your preferences and settings</li>
                <li>Understand how you interact with our website</li>
                <li>Analyze website traffic and performance</li>
                <li>Deliver personalized content and advertisements</li>
              </ul>
              
              <h4 className="font-bold text-white mb-3 mt-6">Types of Cookies We Use</h4>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li><strong className="text-white">Essential Cookies:</strong> Required for basic website functionality</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong className="text-white">Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              </ul>
              <p className="mt-4">You can control cookie preferences through your browser settings. Note that disabling certain cookies may affect website functionality.</p>
            </>
          }
        />

        <Section 
          number="06"
          title="Data Retention"
          icon={<Database className="w-5 h-5" />}
          content={
            <>
              <p>We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
              <p>Generally, we retain:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li><strong className="text-white">Project Data:</strong> For the duration of the project and up to 7 years afterward for legal and accounting purposes</li>
                <li><strong className="text-white">Marketing Data:</strong> Until you unsubscribe or request deletion</li>
                <li><strong className="text-white">Website Analytics:</strong> Typically 26 months</li>
              </ul>
            </>
          }
        />

        <Section 
          number="07"
          title="Your Privacy Rights"
          icon={<UserCheck className="w-5 h-5" />}
          content={
            <>
              <p>You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                <li><strong className="text-white">Objection:</strong> Object to certain processing of your information</li>
                <li><strong className="text-white">Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong className="text-white">Withdrawal of Consent:</strong> Withdraw consent for marketing communications at any time</li>
              </ul>
              <p className="mt-4">To exercise these rights, please contact us at <a href="mailto:privacy@duostacksystems.com" className="text-primary hover:underline">privacy@duostacksystems.com</a></p>
            </>
          }
        />

        <Section 
          number="08"
          title="Third-Party Links"
          icon={<Globe className="w-5 h-5" />}
          content={
            <>
              <p>Our website may contain links to third-party websites or services that are not owned or controlled by DuoStack Systems. We are not responsible for the privacy practices of these third parties.</p>
              <p>We encourage you to review the privacy policies of any third-party sites you visit.</p>
            </>
          }
        />

        <Section 
          number="09"
          title="Children's Privacy"
          icon={<Shield className="w-5 h-5" />}
          content={
            <>
              <p>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children.</p>
              <p>If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information promptly.</p>
            </>
          }
        />

        <Section 
          number="10"
          title="International Data Transfers"
          icon={<Globe className="w-5 h-5" />}
          content={
            <>
              <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws different from those in your country.</p>
              <p>We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.</p>
            </>
          }
        />

        <Section 
          number="11"
          title="Changes to This Privacy Policy"
          icon={<Eye className="w-5 h-5" />}
          content={
            <>
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.</p>
              <p>We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.</p>
            </>
          }
        />

        <Section 
          number="12"
          title="Contact Us"
          icon={<UserCheck className="w-5 h-5" />}
          content={
            <>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="mt-4 space-y-2 text-zinc-400">
                <p><strong className="text-white">Email:</strong> <a href="mailto:privacy@duostacksystems.com" className="text-primary hover:underline">privacy@duostacksystems.com</a></p>
                <p><strong className="text-white">Legal Email:</strong> <a href="mailto:legal@duostacksystems.com" className="text-primary hover:underline">legal@duostacksystems.com</a></p>
                <p><strong className="text-white">Location:</strong> Accra, Ghana</p>
              </div>
            </>
          }
        />
      </div>

      {/* Trust Badge Footer */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-20 bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-3xl p-8 text-center"
      >
        <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-3">Your Trust is Our Priority</h3>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          We are committed to maintaining the highest standards of data protection and privacy. If you have any concerns about how we handle your information, please don't hesitate to reach out.
        </p>
      </motion.div>
    </div>
  );
};

const Section = ({ number, title, icon, content }: { number: string, title: string, icon: React.ReactNode, content: React.ReactNode }) => {
  return (
    <motion.section 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border-l-2 border-zinc-800 pl-8 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-center space-x-4 mb-4">
        <span className="text-xs font-mono font-bold text-zinc-600">{number}</span>
        <div className="text-primary">{icon}</div>
        <h2 className="text-2xl font-display font-bold">{title}</h2>
      </div>
      <div className="space-y-4 text-zinc-400 leading-relaxed">
        {content}
      </div>
    </motion.section>
  );
};

const QuickStat = ({ icon, title, desc }: { icon: React.ReactNode, title: string, desc: string }) => {
  return (
    <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-6 text-center hover:border-primary/30 transition-colors">
      <div className="text-primary mb-3 flex justify-center">{icon}</div>
      <h4 className="font-bold text-white mb-1">{title}</h4>
      <p className="text-sm text-zinc-500">{desc}</p>
    </div>
  );
};

export default PrivacyPolicy;